<template>
  <div class="unsubscribe-page">
    <NavigationBar />

    <main class="unsubscribe-card">
      <h1 class="title">{{ c.heading }}</h1>

      <p v-if="state === 'working'" class="body">{{ c.working }}</p>

      <template v-else-if="state === 'unsubscribed'">
        <h2 class="subtitle">{{ c.successHeading }}</h2>
        <p class="body">{{ c.successBody }}</p>
        <p class="body muted">{{ c.undoPrompt }}</p>
        <button class="btn" :disabled="busy" @click="resubscribe">
          {{ c.undoCta }}
        </button>
      </template>

      <template v-else-if="state === 'resubscribed'">
        <h2 class="subtitle">{{ c.resubscribedHeading }}</h2>
        <p class="body">{{ c.resubscribedBody }}</p>
        <a class="btn" href="https://momfident.io">{{ c.homeCta }}</a>
      </template>

      <template v-else-if="state === 'invalid'">
        <h2 class="subtitle">{{ c.invalidHeading }}</h2>
        <p class="body">{{ c.invalidBody }}</p>
        <a class="btn" href="https://momfident.io">{{ c.homeCta }}</a>
      </template>

      <template v-else>
        <h2 class="subtitle">{{ c.errorHeading }}</h2>
        <p class="body">{{ c.errorBody }}</p>
        <button class="btn" :disabled="busy" @click="unsubscribe">
          {{ c.retryCta }}
        </button>
      </template>
    </main>

    <FooterComponent />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import NavigationBar from "@/components/NavigationBar.vue";
import FooterComponent from "@/components/Components/FooterComponent.vue";
import { apiBaseUrl } from "@/config/api";
import { unsubscribeContent } from "@/i18n/emailPreferences";

const route = useRoute();

// The language comes from the link the email built, not from site locale
// detection — the recipient already told us their language when they signed up.
const lang = computed(() => (route.query.lang === "en" ? "en" : "bg"));
const c = computed(() => unsubscribeContent[lang.value]);

const uid = computed(() =>
  typeof route.query.uid === "string" ? route.query.uid : null
);
const token = computed(() =>
  typeof route.query.t === "string" ? route.query.t : null
);

const state = ref("working"); // working | unsubscribed | resubscribed | invalid | error
const busy = ref(false);

async function call(path) {
  busy.value = true;
  try {
    const res = await fetch(`${apiBaseUrl}/api/emails/${path}`, {
      method: "POST",
      mode: "cors",
      credentials: "omit",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ userId: uid.value, token: token.value }),
    });
    if (res.status === 401) return "invalid";
    if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
    return "ok";
  } catch (err) {
    console.error(`Email preference request failed (${path}):`, err);
    return "error";
  } finally {
    busy.value = false;
  }
}

async function unsubscribe() {
  state.value = "working";
  const result = await call("unsubscribe");
  state.value = result === "ok" ? "unsubscribed" : result;
}

async function resubscribe() {
  const result = await call("resubscribe");
  state.value = result === "ok" ? "resubscribed" : result;
}

onMounted(() => {
  document.title = c.value.metaTitle;

  // A malformed link can't be acted on — say so rather than firing a request
  // that will only come back 401.
  if (!uid.value || !token.value) {
    state.value = "invalid";
    return;
  }
  // Unsubscribe immediately on load: the recipient already expressed intent by
  // clicking "unsubscribe" in the email. Making them click a second button here
  // is the pattern that pushes people to hit "report spam" instead.
  unsubscribe();
});
</script>

<style scoped lang="scss">
@import "~@/assets/scss/variables.scss";

.unsubscribe-page {
  font-family: $font-regular;
  background: #f7f7f9;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.unsubscribe-card {
  flex: 1;
  max-width: 640px;
  width: 100%;
  margin: 48px auto;
  padding: 32px 24px 40px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06);
  text-align: center;
}

.title {
  font-size: 26px;
  font-weight: 700;
  margin-bottom: 24px;
}

.subtitle {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 12px;
}

.body {
  font-size: 16px;
  line-height: 1.55;
  margin-bottom: 16px;

  &.muted {
    color: #767676;
    margin-top: 24px;
    margin-bottom: 8px;
  }
}

.btn {
  display: inline-block;
  background: #6b6b9c;
  color: #ffffff;
  border: none;
  border-radius: 50px;
  padding: 14px 28px;
  font-size: 16px;
  text-decoration: none;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
}
</style>
